<template>
  <div
    ref="elRef"
    class="IsoChat"
    :class="{
      [`IsoChat--type${type}`]: true,
    }"
  >
    <span
      v-for="(fragment) in fragments"
      ref="fragmentBackgroundRefs"
      :key="fragment"
      class="IsoChat__fragmentBackground"
    />

    <span
      v-for="(fragment) in fragments"
      ref="fragmentForegroundRefs"
      :key="fragment"
      class="IsoChat__fragment"
    >
      <UIText
        v-if="elRef"
        class="IsoChat__fragmentInner"
        tag="span"
        weight="Medium"
        isMultiline
      >
        {{ fragment }}
      </UIText>
    </span>
  </div>
</template>

<script lang="ts" setup>
import UIText from "@cosine/components/UIText.vue";
import toggleDataset from "./toggleDataset";
import { easeInCubicString, easeOutCubicString } from "@shared/lib/animation/easing";
import { onBeforeUnmount, onMounted, ref, toRefs } from "vue";

const props = defineProps<{
  type: IsoChatType,
  fragments: Array<string>,
}>();

defineExpose({
  animateIn,
  animateOut,
});

type IsoChatType = "User" | "System";

const {
  type,
  fragments,
} = toRefs(props);

const elRef = ref<HTMLElement>();
const fragmentForegroundRefs = ref<Array<HTMLElement>>([]);
const fragmentBackgroundRefs = ref<Array<HTMLElement>>([]);
const fragmentXOffset = -8;
const fragmentYOffset = -11;
let resizeObserver: ResizeObserver | undefined;

onMounted(() => {
  if (!elRef.value) { return; }

  resizeObserver = new ResizeObserver(handleResize);
  resizeObserver.observe(elRef.value);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
});

function animateIn ({
  index,
}: { index?: number } = {}) {
  if (type.value === "User") {
    return animateUserChatIn();
  } else {
    return animateSystemChatIn({
      index,
    });
  }
}

function animateUserChatIn (): Promise<Animation> {
  if (!elRef.value) { return Promise.reject(new Error("IsoChat has no element")); }

  return elRef.value.animate(
    [
      {
        clipPath: "polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)",
      },
      {
        clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
      },
    ],
    {
      duration: 1000,
      easing: easeOutCubicString,
      fill: "forwards",
    },
  ).finished;
}

function animateSystemChatIn ({
  index,
}: { index?: number }) {
  return Promise.all([animateFragmentIn(fragmentForegroundRefs.value[index || 0]), animateFragmentIn(fragmentBackgroundRefs.value[index || 0])]);
}

function animateFragmentIn (fragment: HTMLElement): Promise<Animation> {
  const top = "calc(0% - 11px)";

  return fragment.animate(
    [
      {
        clipPath: `polygon(0% ${top}, 0% ${top}, 0% 100%, 0% 100%)`,
      },
      {
        clipPath: `polygon(0% ${top}, 100% ${top}, 100% 100%, 0% 100%)`,
      },
    ],
    {
      delay: 50,
      duration: 300,
      easing: easeOutCubicString,
      fill: "forwards",
    },
  ).finished;
}

function animateOut (): Promise<Animation> {
  if (!elRef.value) { return Promise.reject(new Error("Element ref doesn’t exist")); }
  const top = "calc(0% - 11px)";

  return elRef.value.animate(
    [
      {
        clipPath: `polygon(0% ${top}, 100% ${top}, 100% 100%, 0% 100%)`,
      },
      {
        clipPath: `polygon(0% ${top}, 0% ${top}, 0% 100%, 0% 100%)`,
      },
    ],
    {
      duration: 750,
      easing: easeInCubicString,
      fill: "forwards",
    },
  ).finished;
}

function updateWrappedFragments () {
  if (!elRef.value) { return; }

  const {
    top,
    left,
  } = elRef.value.getBoundingClientRect();
  fragmentForegroundRefs.value.forEach((fragment, i) => {
    const {
      top: fragmentTop,
      left: fragmentLeft,
      width: fragmentWidth,
      height: fragmentHeight,
    } = fragment.getBoundingClientRect();

    const isTopLine = fragmentTop === top;
    const isWrapped = fragmentTop > top && fragmentLeft === left;
    const isNotWrapped = fragmentLeft > left;

    const background = fragmentBackgroundRefs.value[i];
    background.style.top = Math.floor(fragmentTop - top + (!isTopLine ? fragmentYOffset : 0)) + "px";
    background.style.left = (fragmentLeft - left) + "px";
    background.style.width = fragmentWidth + "px";
    background.style.height = Math.ceil(fragmentHeight - (!isTopLine ? fragmentYOffset : 0)) + "px";

    toggleDataset(fragment, "jsWrapped", isWrapped);
    toggleDataset(fragment, "jsNotWrapped", isNotWrapped);
    toggleDataset(fragment, "jsFirstLine", isTopLine);
  });
}

function handleResize () {
  // todo: debounce
  updateWrappedFragments();
}
</script>

<style lang="scss" scoped>
.IsoChat {
  position: relative;
  isolation: isolate;

  display: inline-flex;
  flex-wrap: wrap;
}

.IsoChat--typeUser {
  clip-path: polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%);
}

.IsoChat__fragment {
  position: relative;

  & + &[data-js-not-wrapped] {
    margin-left: calc(1px * v-bind(fragmentXOffset));
  }
}

.IsoChat__fragmentBackground {
  position: absolute;
  z-index: -1;
  transform: translate3d(0, 0, 0);

  display: block;

  .IsoChat--typeUser & {
    background: var(--colorWhite);
  }

  .IsoChat--typeSystem & {
    background: var(--colorSwissBlack);
  }
}

.IsoChat__fragment,
.IsoChat__fragmentBackground {
  .IsoChat--typeSystem & {
    clip-path: polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%);
  }
}

.IsoChat__fragmentInner {
  display: block;
  padding: 6px 10px 5px;
  white-space: nowrap;

  .IsoChat--typeUser & {
    color: var(--colorSwissBlack);
  }

  .IsoChat--typeSystem & {
    color: var(--colorWhite);
  }

  .IsoChat__fragment:not([data-js-first-line]) & {
    margin-top: calc(1px * v-bind(fragmentYOffset));
  }

  .IsoChat__fragment[data-js-not-wrapped] & {
    padding-left: 1px;
  }
}
</style>
